import { useEffect, useRef, useState } from "react";
import { useFolderData } from "../data/use-folder-data";
import type { FileDto } from "../lib/gen-api";
import { isNotNullish } from "../lib/nullable";

interface FileParserResult {
  tuples: FileParserTuple[];
  fileUuids: string[];

  /**
   * Ensures that files with the provided UUIDs will no longer be deleted even
   * if the are removed from the files array;
   */
  commitFiles: (fileUuids: string[]) => void;
}

interface FileParserCallbacks {
  /** Called when files are successfully uploaded and added to the server */
  onAdded?: (fileUuids: string[]) => void;

  /** Called when files are removed from the collection */
  onRemoved?: (fileUuids: string[]) => void;
}

export interface FileParserTuple {
  local: File;
  server: FileDto | null;
  isUploading?: boolean;
  isDeleting?: boolean;
  error?: string;
}

export const useFileParser = (
  groupUuid: string | null,
  kitUuid: string | null,
  folderUuid: string | null,
  files: File[],
  callbacks?: FileParserCallbacks,
): FileParserResult => {
  const [fileMap, setFileMap] = useState<Map<File, FileDto>>(new Map());
  const [uploadingFiles, setUploadingFiles] = useState<Set<File>>(new Set());
  const [deletingFiles, setDeletingFiles] = useState<Set<File>>(new Set());
  const [fileErrors, setFileErrors] = useState<Map<File, string>>(new Map());
  const [committedFiles, setCommittedFiles] = useState<Set<string>>(new Set());

  const { folder, createFile, deleteFile } = useFolderData(
    groupUuid,
    kitUuid,
    folderUuid,
  );

  const previousFilesRef = useRef<File[]>([]);

  const handleFileUpload = async (file: File) => {
    try {
      setUploadingFiles((prev) => new Set([...prev, file]));
      setFileErrors((prev) => {
        const newMap = new Map(prev);
        newMap.delete(file);
        return newMap;
      });

      const serverFile = await createFile(file);

      if (serverFile) {
        setFileMap((prev) => new Map([...prev, [file, serverFile]]));

        // Notify callback about successful upload
        callbacks?.onAdded?.([serverFile.uuid]);
      } else {
        throw new Error("File upload returned null");
      }
    } catch (error) {
      console.error("Failed to upload file:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Upload failed";
      setFileErrors((prev) => new Map([...prev, [file, errorMessage]]));
    } finally {
      setUploadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(file);
        return newSet;
      });
    }
  };

  const handleFileDelete = async (file: File, serverFile: FileDto) => {
    try {
      setDeletingFiles((prev) => new Set([...prev, file]));
      setFileErrors((prev) => {
        const newMap = new Map(prev);
        newMap.delete(file);
        return newMap;
      });

      await deleteFile(serverFile.uuid);

      setFileMap((prev) => {
        const newMap = new Map(prev);
        newMap.delete(file);
        return newMap;
      });
    } catch (error) {
      console.error("Failed to delete file:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Delete failed";
      setFileErrors((prev) => new Map([...prev, [file, errorMessage]]));
    } finally {
      setDeletingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(file);
        return newSet;
      });
    }
  };

  // Sync files when the array changes
  useEffect(() => {
    if (!groupUuid || !kitUuid || !folderUuid) {
      return;
    }

    const previousFiles = previousFilesRef.current;
    const currentFiles = files;

    // Find newly added files
    const newFiles = currentFiles.filter(
      (file) => !previousFiles.includes(file),
    );

    // Find removed files
    const removedFiles = previousFiles.filter(
      (file) => !currentFiles.includes(file),
    );

    // Upload new files
    newFiles.forEach((file) => {
      handleFileUpload(file);
    });

    // Delete removed files (skip committed files)
    const removedFileUuids: string[] = [];
    removedFiles.forEach((file) => {
      const serverFile = fileMap.get(file);
      if (serverFile && !committedFiles.has(serverFile.uuid)) {
        removedFileUuids.push(serverFile.uuid);
        handleFileDelete(file, serverFile);
      }
    });

    if (removedFileUuids.length > 0) {
      callbacks?.onRemoved?.(removedFileUuids);
    }

    previousFilesRef.current = currentFiles;
  }, [files, groupUuid, kitUuid, folderUuid, callbacks]);

  // Clean up removed files from internal state
  useEffect(() => {
    const currentFileSet = new Set(files);

    setFileMap((prev) => {
      const newMap = new Map();
      for (const [file, serverFile] of prev) {
        if (currentFileSet.has(file)) {
          newMap.set(file, serverFile);
        }
      }
      return newMap;
    });

    setFileErrors((prev) => {
      const newMap = new Map();
      for (const [file, error] of prev) {
        if (currentFileSet.has(file)) {
          newMap.set(file, error);
        }
      }
      return newMap;
    });

    setUploadingFiles(
      (prev) => new Set([...prev].filter((file) => currentFileSet.has(file))),
    );
    setDeletingFiles(
      (prev) => new Set([...prev].filter((file) => currentFileSet.has(file))),
    );

    // Clean up committed files that are no longer in the files array
    setCommittedFiles((prev) => {
      const existingServerUuids = new Set(
        files
          .map((file) => fileMap.get(file)?.uuid)
          .filter((uuid): uuid is string => !!uuid),
      );
      return new Set([...prev].filter((uuid) => existingServerUuids.has(uuid)));
    });
  }, [files]);

  const tuples = files.map(
    (file): FileParserTuple => ({
      local: file,
      server:
        folder?.files.find((s) => s.uuid === fileMap.get(file)?.uuid) || null,
      isUploading: uploadingFiles.has(file),
      isDeleting: deletingFiles.has(file),
      error: fileErrors.get(file),
    }),
  );

  const fileUuids = tuples.map((t) => t.server?.uuid).filter(isNotNullish);

  const commitFiles = (uuids: string[]) => {
    setCommittedFiles((prev) => new Set([...prev, ...uuids]));
  };

  // Build the result array
  return {
    tuples,
    fileUuids,
    commitFiles,
  };
};
